import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, Layout, message, Table, TableProps, Tag, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, } from '@ant-design/icons';
import type { ColumnsType, SorterResult } from 'antd/es/table/interface';
import { FilterOperatorsEnum, IItemsWithCount, IUserEntity, KeyValueRecord, PermissionsEnum, transformToFormattedTime, UserRolesEnum } from '@rasayi-workspace/shared';
import { MapAntDesignSearchFilter, MapAntDesignSortOrder, RemoveUndefinedKeyPairs } from '@helpers';
import { BASE_QUERY_OPTIONS, DEFAULT_TABLE_STATE } from '@constants';
import { DeleteItem, EMPTY_INITIAL_ITEMS, GetTableItems, HasAnyPermission } from '@services';
import { BaseLayoutComponent, ColumnsSearchProps, DeleteConfirmationModal, TablePageTitleComponent } from '@components';
import { ErrorResponse, ITableState } from '@interfaces';
import { UserModalComponent } from './add-edit';

const { Content } = Layout;

export const UserPageComponent = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const [tableState, setTableState] = useState<ITableState>(DEFAULT_TABLE_STATE);
    const [usersData, setUsersData] = useState<IItemsWithCount<IUserEntity>>(EMPTY_INITIAL_ITEMS);
    const [isUserModalOpened, setIsUserModalOpened] = useState(false);
    const [editingUserId, setEditingUserId] = useState('');

    const { refetch, isFetching } = useQuery<IItemsWithCount<IUserEntity>, AxiosError>({
        ...BASE_QUERY_OPTIONS,
        queryKey: ['users'],
        queryFn: () => GetTableItems<IUserEntity>(
            'user',
            ['roles', 'groups'],
            [],
            tableState,
            [
                [
                    {
                        field: 'roles.name',
                        operator: FilterOperatorsEnum.NOT_EQUAL,
                        value: UserRolesEnum.CUSTOMER
                    }
                ]
            ]
        ),
        onSuccess: (returnedResult: IItemsWithCount<IUserEntity>): void => setUsersData(returnedResult),
        onError: () => setUsersData(EMPTY_INITIAL_ITEMS)
    });

    const { mutate: deleteUser } = useMutation<IUserEntity, AxiosError>({
        mutationKey: ['deleteUser'],
        mutationFn: async () =>
            DeleteItem<IUserEntity>(
                'user',
                editingUserId,
            ),
        onSuccess: async (): Promise<void> => {
            messageApi.open({
                type: 'success',
                content: `User deleted!`,
            });

            refetch();
            resetModal();
        },
        onError: (error: AxiosError) => {
            messageApi.open({
                type: 'error',
                content: (error?.response?.data as ErrorResponse)?.message || 'Contact support for details'
            });
        }
    });

    const handleChange: TableProps<IUserEntity>['onChange'] = (pagination, filters, sorter) => {
        let sortBy: KeyValueRecord = RemoveUndefinedKeyPairs({
            [(sorter as SorterResult<IUserEntity>).columnKey as keyof IUserEntity]: MapAntDesignSortOrder((sorter as SorterResult<IUserEntity>).order)
        });

        sortBy = Object.keys(sortBy).length ? sortBy : DEFAULT_TABLE_STATE.sortBy;

        setTableState({
            ...tableState,
            page: pagination.current || tableState.page,
            pageSize: pagination.pageSize || tableState.pageSize,
            sortBy: sortBy,
            search: MapAntDesignSearchFilter(RemoveUndefinedKeyPairs(filters))
        });
    };

    const onUserModalCloseHandler = (newUser: IUserEntity) => {
        refetch();
        resetModal();

        messageApi.open(
            !editingUserId ? {
                type: 'success',
                content: `New agent '${ newUser?.first_name + ' ' + newUser?.last_name }' created!`,
            } : {
                type: 'success',
                content: `Agent '${ newUser?.first_name + ' ' + newUser?.last_name }' updated!`,
            }
        );
    };

    const onModalCloseHandler = () => resetModal();

    const resetModal = () => {
        setIsUserModalOpened(false);
        setEditingUserId('');
    };

    const resetSearchHandler = (dataIndex: string) => {
        setTableState({
            ...tableState,
            search: tableState.search?.filter(item => !(item.dataKey === dataIndex)) || []
        });
    };

    const columns: ColumnsType<IUserEntity> = [
        {
            title: 'First name',
            dataIndex: 'first_name',
            key: 'first_name',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            ...ColumnsSearchProps('first_name', 'by first name', resetSearchHandler)
        },
        {
            title: 'Last name',
            dataIndex: 'last_name',
            key: 'last_name',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            ...ColumnsSearchProps('last_name', 'by last name', resetSearchHandler)
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            ellipsis: true,
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            ...ColumnsSearchProps('email', 'by email', resetSearchHandler)
        },
        {
            title: 'Roles',
            key: 'roles.name',
            ellipsis: true,
            render: (_: string, item: IUserEntity) => {
                const roles = item.roles?.map(({ name }) => name) || [];

                return <Tooltip placement='topLeft' title={ roles.join(', ') }>
                    <span className='cursor-pointer'>
                        {
                            roles.map((name, index) => <Tag key={ index } color='pink'>{ name }</Tag>)
                        }
                    </span>
                </Tooltip>;
            },
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
            ...ColumnsSearchProps('roles.name', 'by role name', resetSearchHandler)
        },
        {
            title: 'Created at',
            dataIndex: 'created_at',
            key: 'created_at',
            ellipsis: true,
            render: (value: string) => transformToFormattedTime(value),
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Last updated at',
            dataIndex: 'updated_at',
            key: 'updated_at',
            ellipsis: true,
            render: (value: string) => transformToFormattedTime(value),
            sorter: () => 0,
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: 'Actions',
            dataIndex: 'operation',
            key: 'operation',
            render: (_, { id, first_name = '', last_name = '' }) => (
                <div className='lg:flex'>
                    <Button
                        type='text'
                        shape='circle'
                        icon={ <EditOutlined/> }
                        disabled={ !HasAnyPermission([PermissionsEnum.USER_FULL, PermissionsEnum.USER_WRITE]) }
                        onClick={
                            () => {
                                setEditingUserId(id);
                                setIsUserModalOpened(true);
                            }
                        }
                    />
                    <Button
                        className='ml-2'
                        type='text'
                        shape='circle'
                        icon={ <DeleteOutlined/> }
                        disabled={ !HasAnyPermission([PermissionsEnum.USER_FULL, PermissionsEnum.USER_DELETE]) }
                        onClick={
                            () => {
                                setEditingUserId(id);

                                DeleteConfirmationModal({
                                    content: `Are you sure you want to delete user '${ first_name + ' ' + last_name }'?`,
                                    onOk: () => {
                                        deleteUser();
                                    },
                                    onCancel: () => resetModal()

                                });
                            }
                        }
                    />
                </div>
            ),
        }
    ];

    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableState]);

    return (
        <>
            { contextHolder }
            {
                isUserModalOpened &&
                <UserModalComponent
                    editMode={ !!editingUserId }
                    id={ editingUserId }
                    onClose={ onModalCloseHandler }
                    onSubmit={ onUserModalCloseHandler }
                />
            }
            <BaseLayoutComponent children={
                <Content className='m-5 bg-white border rounded-lg'>
                    <Card
                        title={
                            <TablePageTitleComponent
                                title='Agents'
                                addButtonText='Add Agent'
                                disabled={ !HasAnyPermission([PermissionsEnum.USER_FULL, PermissionsEnum.USER_WRITE]) }
                                onClick={ () => setIsUserModalOpened(true) }
                            />
                        }
                    >
                        <div className='border-2 border-black rounded-lg'>
                            <Table
                                columns={ columns }
                                dataSource={ usersData.items }
                                onChange={ handleChange }
                                pagination={
                                    {
                                        total: usersData.count,
                                        pageSize: usersData.pageSize,
                                        current: usersData.page,
                                    }
                                }
                                loading={ isFetching }
                                rowKey={ 'id' }
                                size='small'
                                scroll={ { x: true } }
                            />
                        </div>
                    </Card>
                </Content>
            }
            />
        </>
    );
};